/**
 * Sound manager using the Web Audio API for procedurally generated sounds.
 * No external audio files needed -- all sounds are synthesized.
 */
export class SoundManager {
  private ctx: AudioContext | null = null;
  private enabled: boolean = true;

  init(): void {
    try {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch {
      this.ctx = null;
    }
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  chomp(): void {
    this.playTone(440, 0.05, 'square', 0.15);
    this.playTone(520, 0.06, 'square', 0.1, 0.05);
  }

  powerUp(): void {
    this.playTone(330, 0.1, 'sawtooth', 0.2);
    this.playTone(440, 0.1, 'sawtooth', 0.2, 0.1);
    this.playTone(550, 0.1, 'sawtooth', 0.2, 0.2);
    this.playTone(660, 0.15, 'sawtooth', 0.25, 0.3);
  }

  catPounced(): void {
    this.playTone(600, 0.08, 'sawtooth', 0.2);
    this.playTone(800, 0.12, 'sawtooth', 0.2, 0.08);
  }

  caught(): void {
    for (let i = 0; i < 6; i++) {
      this.playTone(500 - i * 60, 0.1, 'sawtooth', 0.2, i * 0.1);
    }
  }

  levelComplete(): void {
    const notes = [523, 587, 659, 698, 784, 880];
    notes.forEach((f, i) => {
      this.playTone(f, 0.12, 'sine', 0.2, i * 0.12);
    });
  }

  extraLife(): void {
    this.playTone(880, 0.1, 'sine', 0.2);
    this.playTone(1100, 0.15, 'sine', 0.25, 0.1);
  }

  treatCollected(): void {
    this.playTone(700, 0.08, 'sine', 0.15);
    this.playTone(900, 0.1, 'sine', 0.2, 0.08);
  }

  private playTone(
    freq: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    delay: number = 0,
  ): void {
    if (!this.enabled || !this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(volume, this.ctx.currentTime + delay);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + delay + duration);
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.start(this.ctx.currentTime + delay);
    osc.stop(this.ctx.currentTime + delay + duration + 0.01);
  }
}
